import { useState } from "react";

import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import {
  DESK_PHONE_LABEL,
  dialDeviceLabel,
  dialDeviceModifierLabel
} from "components/suno-dialer/call-patient/dial-device-copy";
import type { DialDevice, UserDevice } from "services/dialstack";

import {
  LOG_IN_MENU_LABEL,
  logOutMenuLabel,
  loggingInTitle
} from "./desk-phone/desk-phone-copy";
import type { DeskPhonesController } from "./desk-phone/use-desk-phones.hook";
import {
  callerIdOptionNumberSx,
  callerIdOptionSx,
  dialDeviceFieldSx,
  dialDeviceRowSx,
  e911SelectMenuSx
} from "./dialstack-softphone-widget.styles";
import { useDialstackSoftphone } from "./use-dialstack-softphone.hook";

export const DIAL_DEVICE_LABEL = "Call using";
export const COMPUTER_DEVICE_LABEL = "This computer";
export const DESK_PHONE_OFFLINE_SUFFIX = " (offline)";
export const DESK_PHONE_OFFLINE_HINT = "Not connected — check it's plugged in";
// A cordless handset has no cable to check: it is off, flat, or out of range
// of its base.
export const CORDLESS_PHONE_OFFLINE_HINT =
  "Not connected — check it's on and near its base";
export const LOGGED_IN_SUFFIX = " · logged in";
// Where focus returns when the desk-phone picker closes: the row's combobox.
export const DIAL_DEVICE_ROW_ID = "suno-dial-device-row";

// Menu entries that are actions, not devices (ENG-2293). Never stored.
const LOG_IN_VALUE = "__log_in__";
const LOG_OUT_VALUE = "__log_out__";

// "Front desk" / "Front desk, Booth 2" — the handset(s) the option rings; a
// phone the user is logged in on says so.
export function deskPhoneNames(devices: UserDevice[]) {
  return devices
    .map((device) =>
      device.name
        ? `${device.name}${device.logged_in ? LOGGED_IN_SUFFIX : ""}`
        : null
    )
    .filter((name): name is string => Boolean(name))
    .join(", ");
}

export const dialDeviceShortcutHint = () =>
  `Hold ${dialDeviceModifierLabel()} while clicking a phone icon to use the other one for a single call.`;

type Props = {
  // Hot desking (ENG-2293): with the practice's desk phones known, the menu
  // also offers to log in on one, and to log out of one logged in on earlier.
  deskPhones?: DeskPhonesController;
  onLogIn?: () => void;
  onLogOut?: () => void;
};

// Which of the user's devices dials from the app go to (ENG-2183). Shown when
// the user has a desk phone to choose — or, since ENG-2293, when the practice
// has desk phones to log in on. The choice is per browser and sticks; holding
// Option/Alt on any phone icon flips it for one call — the row's tooltip is
// where that shortcut is discoverable. One line, so it costs the panel no more
// room than the "Call from" row above the keypad.
export default function DialDeviceSelect({
  deskPhones,
  onLogIn,
  onLogOut
}: Props = {}) {
  const { devices, dialDevice, setDialDevice, refreshDevices } =
    useDialstackSoftphone();
  const [menuOpen, setMenuOpen] = useState(false);
  const hasDeskPhone = devices.length > 0;
  // A failed listing still gets the entry: the picker is where "Try again" is.
  const canLogIn = Boolean(
    deskPhones &&
      onLogIn &&
      (deskPhones.phones.length > 0 || deskPhones.isError)
  );
  const session =
    deskPhones?.mySession && onLogOut ? deskPhones.mySession : null;
  // While a log-in is in flight the row says so, instead of "This computer".
  const loggingIn =
    deskPhones?.request?.phase === "loggingIn"
      ? deskPhones.request.phone.name
      : null;
  if (!hasDeskPhone && !canLogIn) return null;

  const deskPhoneOnline = devices.some((device) => device.online);
  const names = deskPhoneNames(devices);
  // "Desk phone" or, for a user whose phones are all cordless handsets,
  // "Cordless phone" (ENG-2539).
  const phoneLabel = dialDeviceLabel(devices);
  const deskPhoneLabel = deskPhoneOnline
    ? phoneLabel
    : `${phoneLabel}${DESK_PHONE_OFFLINE_SUFFIX}`;
  const offlineHint =
    phoneLabel === DESK_PHONE_LABEL
      ? DESK_PHONE_OFFLINE_HINT
      : CORDLESS_PHONE_OFFLINE_HINT;

  const handleChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    if (value === LOG_IN_VALUE) {
      onLogIn?.();
      return;
    }
    if (value === LOG_OUT_VALUE) {
      onLogOut?.();
      return;
    }
    setDialDevice(value as DialDevice);
  };

  return (
    // An empty title renders no tooltip: the hint must not sit over the open
    // menu, whose options are what the user is reading at that moment — and
    // without a desk phone there is no other device to flip to.
    <Tooltip
      disableInteractive
      title={menuOpen || !hasDeskPhone ? "" : dialDeviceShortcutHint()}
    >
      <Box id={DIAL_DEVICE_ROW_ID} sx={dialDeviceRowSx}>
        <Typography component="span" id="suno-dial-device-label">
          {DIAL_DEVICE_LABEL}
        </Typography>
        <Select
          variant="standard"
          disableUnderline
          labelId="suno-dial-device-label"
          value={dialDevice}
          renderValue={(selected) =>
            loggingIn
              ? loggingInTitle(loggingIn)
              : selected === "deskphone"
                ? `${deskPhoneLabel}${names ? ` · ${names}` : ""}`
                : COMPUTER_DEVICE_LABEL
          }
          onChange={handleChange}
          onOpen={() => {
            setMenuOpen(true);
            // The user is looking for a phone right now: a handset assigned or
            // plugged in since the page loaded must be in the list they see.
            refreshDevices();
          }}
          onClose={() => setMenuOpen(false)}
          MenuProps={{ sx: e911SelectMenuSx }}
          inputProps={{ "aria-label": DIAL_DEVICE_LABEL }}
          sx={dialDeviceFieldSx}
        >
          <MenuItem value="softphone" sx={callerIdOptionSx}>
            <span>{COMPUTER_DEVICE_LABEL}</span>
          </MenuItem>
          {/* Unplugged handsets cannot be chosen: a dial to one rings nothing. */}
          {hasDeskPhone && (
            <MenuItem
              value="deskphone"
              disabled={!deskPhoneOnline}
              sx={callerIdOptionSx}
            >
              <span>{deskPhoneLabel}</span>
              <Typography component="span" noWrap sx={callerIdOptionNumberSx}>
                {deskPhoneOnline ? names : offlineHint}
              </Typography>
            </MenuItem>
          )}
          {/* A rule above the actions, not a Divider: a Divider inside a Select
            becomes an empty option to assistive tech. */}
          {canLogIn && (
            <MenuItem
              value={LOG_IN_VALUE}
              sx={[callerIdOptionSx, { borderTop: 1, borderColor: "divider" }]}
            >
              <span>{LOG_IN_MENU_LABEL}</span>
            </MenuItem>
          )}
          {session && (
            <MenuItem value={LOG_OUT_VALUE} sx={callerIdOptionSx}>
              <span>{logOutMenuLabel(session.device_name)}</span>
            </MenuItem>
          )}
        </Select>
      </Box>
    </Tooltip>
  );
}
